import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, PopoverGeneral } from '@spark';
import ImprovementModeTourSpotlight from '@/features/datasets/components/DataSelection/ImprovementModeTourSpotlight';
import '@/features/datasets/components/DataSelection/ImprovementMode.css';

const IMPROVEMENT_MODE_TOUR_BODY_CLASS = 'improvement-mode-tour-active';

const ImprovementModeTour = ({ cardRef, minValidationsPerLabel, open, onClose }) => {
    const [pos, setPos] = useState(null);
    const popoverRef = useRef(null);

    const handleTourClose = () => {
        onClose?.();
        const url = new URL(window.location.href);
        url.searchParams.delete('fromEvaluation');
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };
    const closeRef = useRef(handleTourClose);
    closeRef.current = handleTourClose;

    useEffect(() => {
        if (!open) return undefined;
        document.body.classList.add(IMPROVEMENT_MODE_TOUR_BODY_CLASS);
        return () => document.body.classList.remove(IMPROVEMENT_MODE_TOUR_BODY_CLASS);
    }, [open]);

    // Close on Escape or on any pointer press outside the popover panel.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') closeRef.current();
        };
        const onPointerDown = (event) => {
            const panel = popoverRef.current;
            if (panel && !panel.contains(event.target)) closeRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [open]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const targetEl = cardRef?.current;
        if (!targetEl?.scrollIntoView) return undefined;
        const nativeScrollIntoView = targetEl.scrollIntoView.bind(targetEl);
        targetEl.scrollIntoView = () => {};
        return () => {
            targetEl.scrollIntoView = nativeScrollIntoView;
        };
    }, [open, cardRef]);

    useLayoutEffect(() => {
        if (!open) { setPos(null); return undefined; }
        let rafId = null;
        // Coalesce measurements instead of cancel-and-reschedule: scroll/resize
        // events fire before rAF callbacks within a frame, so cancelling the
        // pending frame on every event can starve the measurement forever
        // (e.g. during the smooth scroll-into-view right after mount).
        const update = () => {
            if (rafId != null) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const el = cardRef?.current;
                if (!el) { setPos(null); return; }
                const rect = el.getBoundingClientRect();
                setPos((prev) => (
                    prev && prev.left === rect.left && prev.top === rect.top - 8
                        ? prev
                        : { left: rect.left, top: rect.top - 8 }
                ));
            });
        };
        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        return () => {
            if (rafId != null) cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [open, cardRef]);

    if (!open || !pos) return <ImprovementModeTourSpotlight open={open} targetRef={cardRef} />;

    return (
        <>
            <ImprovementModeTourSpotlight open={open} targetRef={cardRef} />
            {createPortal(
                <div
                    ref={popoverRef}
                    className="improvement-mode-tour"
                    style={{
                        position: 'fixed',
                        left: pos.left,
                        top: pos.top,
                        transform: 'translateY(-100%)',
                        zIndex: 1401,
                    }}
                >
                    <PopoverGeneral
                        tip
                        tipDirection="bottom"
                        header
                        footer={false}
                        title="Start improving your model"
                        showMinimize={false}
                        showClose
                        onCloseClick={handleTourClose}
                    >
                        <p className="improvement-mode-tour__text">
                            Review at least {minValidationsPerLabel} examples per label for optimal results
                        </p>
                        <Button
                            variant="link"
                            size="small"
                            type="button"
                            className="improvement-mode-tour__cta"
                            onClick={handleTourClose}
                        >
                            Begin review
                        </Button>
                    </PopoverGeneral>
                </div>,
                document.body,
            )}
        </>
    );
};

export default ImprovementModeTour;
